import { Alert, Button, SearchInput } from '@components'
import { Spinner } from '@components/ui/Primitives'
import { ILucaWorkflowTool, MAX_LUCA_APPROVED_TOOLS } from '@query'
import Color from 'color'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaBook,
  FaChartBar,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaFileInvoice,
  FaPlus,
  FaTools,
  FaUserShield
} from 'react-icons/fa'
import type { IconType } from 'react-icons'
import styled, { css } from 'styled-components'
import { filterLucaToolGroups, groupLucaTools, orderSelectedLucaTools } from './lucaToolPickerUtils'

const categoryIcon = (category: string): IconType => {
  const normalized = category.toLocaleLowerCase()
  if (normalized.includes('invoice') || normalized.includes('lasku')) return FaFileInvoice
  if (normalized.includes('book') || normalized.includes('kirjan')) return FaBook
  if (normalized.includes('report') || normalized.includes('tilika')) return FaChartBar
  return FaTools
}

interface LucaApprovedToolsPickerProps {
  nodeId: string
  selected: string[]
  catalogTools: ILucaWorkflowTool[]
  showToolsLoading: boolean
  showToolsError: boolean
  toolsLoaded: boolean
  onCommit: (nextSelected: string[]) => void
  onRetry: () => void
}

export const LucaApprovedToolsPicker: React.FC<LucaApprovedToolsPickerProps> = ({
  nodeId,
  selected,
  catalogTools,
  showToolsLoading,
  showToolsError,
  toolsLoaded,
  onCommit,
  onRetry
}) => {
  const [t] = useTranslation()
  const [query, setQuery] = useState('')
  const [openCategories, setOpenCategories] = useState<Set<string> | null>(null)
  const groupedTools = useMemo(() => groupLucaTools(catalogTools), [catalogTools])
  const filteredGroups = useMemo(
    () => filterLucaToolGroups(groupedTools, query),
    [groupedTools, query]
  )
  const catalogNames = new Set(catalogTools.map(tool => tool.name))
  const unknownNames = selected.filter(name => !catalogNames.has(name))
  const atLimit = selected.length >= MAX_LUCA_APPROVED_TOOLS
  const selectedSet = new Set(selected)
  const defaultOpenCategory =
    groupedTools.find(group => group.tools.some(tool => selectedSet.has(tool.name)))?.category ??
    groupedTools[0]?.category
  const activeOpenCategories = query.trim()
    ? new Set(filteredGroups.map(group => group.category))
    : openCategories ?? new Set(defaultOpenCategory ? [defaultOpenCategory] : [])

  const commitSelected = (nextSelected: string[]) => {
    onCommit(orderSelectedLucaTools(nextSelected, catalogTools))
  }

  const selectTool = (tool: ILucaWorkflowTool) => {
    if (selectedSet.has(tool.name)) {
      commitSelected(selected.filter(name => name !== tool.name))
      return
    }
    if (atLimit) return
    commitSelected([...selected, tool.name])
  }

  return (
    <ToolSection>
      <Divider />
      <SectionHeading>
        <SectionTitle>{t('workflows.config.luca.approvedTools')}</SectionTitle>
        <HeadingMeta>
          <FooterMeta $atLimit={atLimit}>
            {t('workflows.config.luca.approvedToolsCounter', {
              count: selected.length,
              max: MAX_LUCA_APPROVED_TOOLS
            })}
          </FooterMeta>
          {selected.length > 0 && (
            <ClearButton type="button" onClick={() => commitSelected([])}>
              {t('workflows.config.luca.approvedToolsClear')}
            </ClearButton>
          )}
        </HeadingMeta>
      </SectionHeading>
      <HelperText>{t('workflows.config.luca.approvedToolsHint')}</HelperText>

      <SearchInput
        name={`workflow-luca-tool-search-${nodeId}`}
        aria-label={t('workflows.config.luca.approvedToolsSearch')}
        placeholder={t('workflows.config.luca.approvedToolsSearch')}
        onSearch={setQuery}
        delay={0}
      />

      {showToolsLoading && (
        <LoadingRow aria-label={t('workflows.config.luca.approvedToolsLoading')}>
          <Spinner size={24} />
        </LoadingRow>
      )}
      {showToolsError && (
        <ErrorStack>
          <Alert
            type="error"
            marginBottom={0}
            description={t('workflows.config.luca.approvedToolsError')}
          />
          <Button type="button" size="sm" isSecondary onClick={onRetry}>
            {t('workflows.config.luca.approvedToolsRetry')}
          </Button>
        </ErrorStack>
      )}
      {toolsLoaded && catalogTools.length === 0 && unknownNames.length === 0 && (
        <EmptyState>{t('workflows.config.luca.approvedToolsEmpty')}</EmptyState>
      )}
      {atLimit && (
        <LimitText role="status">
          {t('workflows.config.luca.approvedToolsLimit', { max: MAX_LUCA_APPROVED_TOOLS })}
        </LimitText>
      )}

      {unknownNames.length > 0 && (
        <UnknownNotice>
          <span>
            {t('workflows.config.luca.approvedToolsUnknownCount', {
              count: unknownNames.length
            })}
          </span>
          <ClearButton
            type="button"
            onClick={() => commitSelected(selected.filter(name => catalogNames.has(name)))}
          >
            {t('workflows.config.luca.approvedToolsUnknownRemove')}
          </ClearButton>
        </UnknownNotice>
      )}

      {!showToolsLoading &&
        !showToolsError &&
        filteredGroups.map(group => {
          const isOpen = activeOpenCategories.has(group.category)
          const selectedCount = group.tools.filter(tool => selectedSet.has(tool.name)).length
          const CategoryIcon = categoryIcon(group.category)

          return (
            <CategoryCard key={group.category}>
              <CategoryButton
                type="button"
                aria-expanded={isOpen}
                onClick={() => {
                  setOpenCategories(current => {
                    const next = new Set(current ?? activeOpenCategories)
                    if (next.has(group.category)) next.delete(group.category)
                    else next.add(group.category)
                    return next
                  })
                }}
              >
                <CategoryIconTile>
                  <CategoryIcon />
                </CategoryIconTile>
                <CategoryName>{group.category}</CategoryName>
                <CategoryCount $selected={selectedCount > 0}>
                  {selectedCount} / {group.tools.length}
                </CategoryCount>
                {isOpen ? <FaChevronDown /> : <FaChevronRight />}
              </CategoryButton>

              {isOpen && (
                <CategoryBody>
                  <ChipList>
                    {group.tools.map(tool => {
                      const isSelected = selectedSet.has(tool.name)
                      return (
                        <ToolChip
                          key={tool.name}
                          type="button"
                          aria-pressed={isSelected}
                          disabled={!isSelected && atLimit}
                          $selected={isSelected}
                          onClick={() => selectTool(tool)}
                        >
                          {isSelected ? <FaCheck /> : <FaPlus />}
                          {tool.title || tool.name}
                        </ToolChip>
                      )
                    })}
                  </ChipList>
                </CategoryBody>
              )}
            </CategoryCard>
          )
        })}

      {!showToolsLoading && !showToolsError && query.trim() && filteredGroups.length === 0 && (
        <EmptyState>{t('workflows.config.luca.approvedToolsNoResults', { query })}</EmptyState>
      )}

      <OwnerNote>
        <FaUserShield />
        <span>{t('workflows.config.luca.approvedToolsOwnerNote')}</span>
      </OwnerNote>
    </ToolSection>
  )
}

const ToolSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const Divider = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  margin: ${({ theme }) => `${theme.spacing.xs}rem 0`};
`

const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const HeadingMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const FooterMeta = styled.span<{ $atLimit: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme, $atLimit }) => ($atLimit ? theme.colors.error : theme.colors.iconGray)};
  font-variant-numeric: tabular-nums;
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
`

const ClearButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  cursor: pointer;
`

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.4;
`

const CategoryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const CategoryButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => `${theme.spacing.sm}rem ${theme.spacing.md}rem`};
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.iconGray};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  > svg:last-child {
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
  }
`

const CategoryIconTile = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.iconSize.sm}rem;
  height: ${({ theme }) => theme.iconSize.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  flex-shrink: 0;

  svg {
    width: ${({ theme }) => theme.fontSize.px11}rem;
    height: ${({ theme }) => theme.fontSize.px11}rem;
  }
`

const CategoryName = styled.span`
  flex: 1;
  min-width: 0;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
`

const CategoryCount = styled.span<{ $selected: boolean }>`
  padding: ${({ theme }) => `${theme.spacing.xxxs}rem ${theme.spacing.xs}rem`};
  border-radius: 999px;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.dashboardExtra1 : theme.colors.iconGray};
  background: ${({ theme, $selected }) =>
    $selected ? Color(theme.colors.primary).alpha(0.1).string() : theme.colors.neutralGray};
`

const CategoryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const selectedChipStyles = css`
  color: ${({ theme }) => theme.colors.dashboardExtra1};
  border-color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => Color(theme.colors.primary).alpha(0.1).string()};
`

const ToolChip = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => `${theme.spacing.xs}rem ${theme.spacing.sm}rem`};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: background 120ms, border-color 120ms;

  ${({ $selected }) => $selected && selectedChipStyles}

  svg {
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const OwnerNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin: ${({ theme }) => `${theme.spacing.xs}rem 0 0`};
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`

const UnknownNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`

const LimitText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
`

const EmptyState = styled.p`
  margin: ${({ theme }) => `${theme.spacing.sm}rem 0`};
  text-align: center;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
`

const ErrorStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md}rem 0;
`
